import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Radio } from "components/radio";
import { Field, useFormikContext } from "formik";
import { useEffect, useRef } from "react";
import { formatCurrency } from "utils/currencyUtils";
import * as Yup from "yup";

// AKT-8286: the minimum comes from the creditor's payment rule (served per account
// by the lookup endpoint), and never exceeds the balance being paid — otherwise an
// account whose balance dropped below the minimum could not be paid off at all.
const getEffectiveMinimum = (minimumAmount, totalAmount) =>
  Math.min(minimumAmount ?? 1, totalAmount || minimumAmount || 1);

export const getAmountFieldsSchema = ({ totalAmount, minimumAmount }) =>
  Yup.object().shape({
    selectedPaymentAmount: Yup.string().oneOf(["full_amount", "partial_amount"]).required(),
    paymentIntent: Yup.object().shape({
      totalAmount: Yup.number()
        .min(
          getEffectiveMinimum(minimumAmount, totalAmount),
          `Partial amount must be at least ${formatCurrency(
            getEffectiveMinimum(minimumAmount, totalAmount),
          )}`,
        )
        // AKT-8303: cap the amount at the selected accounts' balance so consumers
        // cannot overpay through the public portal. `.max` is inclusive, so paying
        // the balance in full is still allowed. The backend enforces the same rule
        // (bulk_create_payment_intents) as the authoritative guard.
        .max(
          totalAmount,
          `Amount cannot exceed the balance of ${formatCurrency(
            totalAmount,
          )}. Overpayments are not allowed.`,
        )
        .required("Required"),
    }),
  });

export function AmountFields({ totalAmount, minimumAmount }) {
  const { values, setFieldValue } = useFormikContext();
  const totalAmountInputRef = useRef(null);

  const focusTotalAmountInput = () => {
    if (totalAmountInputRef.current) {
      // making sure the input is focused after the render
      setTimeout(() => {
        totalAmountInputRef.current.focus();
      }, 0);
    }
  };

  // Reset total amount field when total amount changes, for example when a new payment is selected
  useEffect(() => {
    setFieldValue("paymentIntent.totalAmount", totalAmount);
  }, [setFieldValue, totalAmount]);

  const radioOnChange = (val) => {
    if (val === "full_amount") {
      setFieldValue("paymentIntent.totalAmount", totalAmount);
    }
    if (val === "partial_amount") {
      setFieldValue("paymentIntent.totalAmount", "");
      focusTotalAmountInput();
    }
  };

  const paymentAmountOptions = [
    {
      label: (
        <HStack>
          <Text>Full Balance {totalAmount ? `(${formatCurrency(totalAmount)})` : null}</Text>
        </HStack>
      ),
      value: "full_amount",
    },
    {
      label: (
        <HStack>
          <Text>Partial Payment</Text>
        </HStack>
      ),
      value: "partial_amount",
    },
  ];

  return (
    <VStack spacing={6} my={[6]}>
      <Field name="selectedPaymentAmount">
        {({ field, form }) => (
          <FormControl
            isInvalid={form.errors.selectedPaymentAmount && form.touched.selectedPaymentAmount}
          >
            <FormLabel>Payment Type</FormLabel>
            <Radio
              form={form}
              field={field}
              options={paymentAmountOptions}
              onChangeCallback={radioOnChange}
            />
          </FormControl>
        )}
      </Field>
      <Field name="paymentIntent.totalAmount">
        {({ field, form }) => (
          <FormControl
            isInvalid={
              form.errors.paymentIntent?.totalAmount && form.touched.paymentIntent?.totalAmount
            }
            hidden={values.selectedPaymentAmount !== "partial_amount"}
          >
            <FormLabel>Partial Payment Amount</FormLabel>
            <NumberInput
              value={field.value}
              onChange={(val) => form.setFieldValue(field.name, val)}
              step={0.01}
              // Deliberately not the rule minimum: NumberInput clamps on blur, which
              // would silently rewrite a below-minimum amount instead of telling the
              // consumer why it is not allowed. The Yup rule surfaces the message.
              min={0}
              precision={2}
              max={totalAmount}
            >
              <NumberInputField
                {...field}
                placeholder="Enter amount..."
                ref={totalAmountInputRef}
              />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormErrorMessage>{form.errors.paymentIntent?.totalAmount}</FormErrorMessage>
          </FormControl>
        )}
      </Field>
    </VStack>
  );
}
